/** Add / Edit Member — port of the original memberModal(). With `projectId`, the new member is also added to that project. */
import { useState } from 'react';
import type { MemberInput } from '@/types/models';
import { LIMITS, validateMember, type FieldErrors } from '@/domain/validation';
import { useAction, useMembers } from '@/hooks/data';
import { ErrorState, Field, LoadingState, ModalFrame } from '@/ui/kit';

export function MemberFormModal({ id, projectId, onClose }: { id?: string; projectId?: string; onClose: () => void }) {
  const q = useMembers();
  const existing = id ? q.data?.find((m) => m.id === id) : undefined;
  const title = `${id ? 'Edit' : 'Add'} Member`;
  if (id) {
    if (q.error) return <ModalFrame title={title} onClose={onClose}><ErrorState error={q.error} onRetry={() => void q.refetch()} /></ModalFrame>;
    if (!q.data) return <ModalFrame title={title} onClose={onClose}><LoadingState /></ModalFrame>;
    if (!existing) return <ModalFrame title={title} onClose={onClose}><div className="empty">No records found.</div></ModalFrame>;
  }
  return <MemberForm key={id || 'new'} id={id} projectId={projectId} initial={{ name: existing?.name || '', role: existing?.role || '' }} title={title} onClose={onClose} />;
}

function MemberForm({ id, projectId, initial, title, onClose }: { id?: string; projectId?: string; initial: MemberInput; title: string; onClose: () => void }) {
  const [f, setF] = useState<MemberInput>(initial);
  const [err, setErr] = useState<FieldErrors>({});
  const set = (k: keyof MemberInput) => (e: React.ChangeEvent<HTMLInputElement>) => setF({ ...f, [k]: e.target.value });
  // A duplicate name is rejected by the provider ("A member with this name already exists."); useAction shows it as an error toast.
  const save = useAction(
    async (s, input: MemberInput) => {
      if (id) return s.members.update(id, input);
      const m = await s.members.create(input);
      if (projectId) {
        const p = await s.projects.get(projectId);
        if (p && !p.members.includes(m.id)) await s.projects.update(projectId, { members: [...p.members, m.id] });
      }
      return m;
    },
    id ? 'Member updated successfully.' : 'Member created successfully.',
  );
  const submit = async () => {
    const e = validateMember(f);
    setErr(e);
    if (Object.keys(e).length) return;
    const ok = await save.run({ name: f.name.trim(), role: f.role.trim() });
    if (ok) onClose();
  };
  return (
    <form onSubmit={(e) => (e.preventDefault(), void submit())} noValidate>
      <ModalFrame
        title={title}
        onClose={onClose}
        footer={
          <>
            <button type="button" className="btn" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn primary" disabled={save.pending}>
              {id ? 'Update' : 'Create'}
            </button>
          </>
        }
      >
        <Field label="Full Name" required error={err.name}>
          <input name="name" value={f.name} onChange={set('name')} placeholder="Enter Full Name" maxLength={LIMITS.memberName} />
        </Field>
        <Field label="Role / Team">
          <input name="role" value={f.role} onChange={set('role')} placeholder="e.g. Developer, Support Team" maxLength={LIMITS.role} />
        </Field>
      </ModalFrame>
    </form>
  );
}
